import { GenderOptions } from "Constants/constant";
import CustomButton from "components/common/Button/CustomButton";
import DateRangePickerNew from "components/common/DateRangePicker/DateRangePickerNew";
import SelectOption from "components/common/Select/Select";
import { AiOutlineInfoCircle, AiOutlinePlus, AiOutlineWarning } from "react-icons/ai";
import { FaTrash } from "react-icons/fa";
import { YesNoToggle } from "../forms/YesNoToggle";
import useCareLabels from "hooks/useCareLabels";

const MAX_WITNESSES = 5;

export const IncidentDetailsStep = ({
  selectedOutcome,
  register,
  watch,
  setValue,
  witnessFields,
  appendWitness,
  removeWitnessField,
}) => {
  const { participantLabel } = useCareLabels();
  // Only show if outcome is Hazard, Near Miss, or Incident (not Medication)
  const shouldShowStep =
    selectedOutcome &&
    ["hazard", "near_miss", "incident"].includes(selectedOutcome.value);

  if (!shouldShowStep) {
    return null;
  }

  return (
    <div className="space-y-6 animate-fadeIn">
      {/* Instructional Banner - Compact */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-start gap-2">
        <AiOutlineInfoCircle className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
        <p className="text-xs text-blue-700">
          Fill out the Medication Incident form if ONLY a medication incident is
          involved; otherwise, complete this section.
        </p>
      </div>

      {/* Section: Incident Summary/Allegation */}
      <section>
        <label className="block text-sm font-medium text-gray-900 mb-2">
          Summarise the incident and/or allegation (without reference to
          people's names)
        </label>
        <textarea
          {...register("executive_summary")}
          rows={3}
          className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm resize-y"
          placeholder="Summarise the incident/allegation..."
        />
      </section>

      {/* Section: Behavioral Context - Horizontal Layout */}
      <section>
        <h3 className="text-sm font-semibold text-gray-900 mb-3">
          Behavioral Context
        </h3>
        <div className="space-y-3">
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <span className="text-sm font-medium text-gray-700">
              Does this person have a behaviour management plan?
            </span>
            <YesNoToggle
              name="has_behavior_management_plan"
              value={watch("has_behavior_management_plan")}
              onChange={(val) => setValue("has_behavior_management_plan", val)}
            />
          </div>
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <span className="text-sm font-medium text-gray-700">
              Were any regulated restrictive practices used?
            </span>
            <YesNoToggle
              name="regulated_restrictive_practices_used"
              value={watch("regulated_restrictive_practices_used")}
              onChange={(val) =>
                setValue("regulated_restrictive_practices_used", val)
              }
            />
          </div>
        </div>
      </section>

      {/* Section: The Narrative - Compact Grid Layout */}
      <section>
        {/* <h3 className="text-sm font-semibold text-gray-900 mb-3">
          The Narrative (Chronological Order)
        </h3> */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex flex-col">
            <div className="min-h-[4rem] mb-1.5">
              <label className="block text-sm font-medium text-gray-900">
                What task was being performed or what the {participantLabel.toLowerCase()} was doing
                at the time of the incident?
              </label>
            </div>
            <textarea
              {...register("current_task")}
              rows={3}
              className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm resize-y"
              placeholder={`Describe what the ${participantLabel.toLowerCase()} was doing...`}
            />
          </div>
          <div className="flex flex-col">
            <div className="min-h-[4rem] mb-1.5">
              <label className="block text-sm font-medium text-gray-900">
                How did the incident end?
              </label>
            </div>
            <textarea
              {...register("conclusion")}
              rows={3}
              className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm resize-y"
              placeholder="Describe how the incident ended..."
            />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-900 mb-1.5">
              What happened? (The details of the incident)
            </label>
            <textarea
              {...register("incident_details_narrative")}
              rows={3}
              className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm resize-y"
              placeholder="Describe what happened in detail..."
            />
          </div>
          <div className="flex flex-col">
            <div className="min-h-[4rem] mb-1.5">
              <label className="block text-sm font-medium text-gray-900">
                What were the circumstances leading up to the incident? Are
                there any triggers?
              </label>
            </div>
            <textarea
              {...register("triggers_circumstances")}
              rows={3}
              className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm resize-y"
              placeholder="Describe what led up to the incident..."
            />
          </div>
          <div className="flex flex-col">
            <div className="min-h-[4rem] mb-1.5">
              <label className="block text-sm font-medium text-gray-900">
                What immediate action was taken?
              </label>
            </div>
            <textarea
              {...register("immediate_action_taken")}
              rows={3}
              className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm resize-y"
              placeholder="Describe the immediate actions taken..."
            />
          </div>
        </div>
      </section>

      {/* Section: Witnesses of the Incident */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-semibold text-gray-900">
              Witnesses of the Incident (if any)
            </h3>
            <span className="text-xs text-green-600 font-medium">
              (Add witness with add another option)
            </span>
            <span className="text-xs text-gray-500">
              ({witnessFields.length}/{MAX_WITNESSES})
            </span>
          </div>
          <CustomButton
            type="button"
            variant="secondary"
            label="Add Another Witness"
            Icon={<AiOutlinePlus />}
            isDisabled={witnessFields.length >= MAX_WITNESSES}
            clickHandler={() =>
              appendWitness({
                first_name: "",
                surname: "",
                date_of_birth: null,
                gender: null,
                address: "",
              })
            }
          />
        </div>

        {/* Max witnesses reached message */}
        {witnessFields.length >= MAX_WITNESSES && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 flex items-center gap-2">
            <AiOutlineWarning className="w-4 h-4 text-amber-600 flex-shrink-0" />
            <p className="text-xs text-amber-700">
              Maximum of {MAX_WITNESSES} witnesses allowed. Remove an existing witness to add a new one.
            </p>
          </div>
        )}

        <div
          className="space-y-4 pr-4 max-h-96 overflow-y-auto scrollbar-thin"
          style={{
            scrollbarColor: "#38528A transparent",
            scrollbarWidth: "thin",
          }}
        >
          {witnessFields.map((field, index) => {
            const witnessDateOfBirth = watch(
              `incident_details_witnesses.${index}.date_of_birth`
            );
            const witnessGender = watch(
              `incident_details_witnesses.${index}.gender`
            );

            return (
              <div
                key={field.id}
                className="bg-gray-50 border border-gray-200 rounded-lg p-4"
              >
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-semibold text-gray-900">
                    Witness {index + 1}
                  </h4>
                  {index > 0 && (
                    <button
                      type="button"
                      onClick={() => removeWitnessField(index)}
                      className="text-gray-400 hover:text-red-500 transition-colors cursor-pointer p-1"
                      aria-label={`Remove Witness ${index + 1}`}
                    >
                      <FaTrash className="w-4 h-4" />
                    </button>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-900 mb-1.5">
                      First Name:
                    </label>
                    <input
                      type="text"
                      {...register(
                        `incident_details_witnesses.${index}.first_name`
                      )}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                      placeholder="Enter first name..."
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-900 mb-1.5">
                      Surname:
                    </label>
                    <input
                      type="text"
                      {...register(
                        `incident_details_witnesses.${index}.surname`
                      )}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                      placeholder="Enter surname..."
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-900 mb-1.5">
                      Date of Birth:
                    </label>
                    <DateRangePickerNew
                      isSingleDate
                      singleSelectedDate={
                        witnessDateOfBirth ? new Date(witnessDateOfBirth) : null
                      }
                      handleChange={(date) => {
                        setValue(
                          `incident_details_witnesses.${index}.date_of_birth`,
                          date
                        );
                      }}
                      handleCancel={() => {
                        setValue(
                          `incident_details_witnesses.${index}.date_of_birth`,
                          null
                        );
                      }}
                      maxDate={new Date()}
                      placeholder="Select date of birth"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-900 mb-1.5">
                      Gender:
                    </label>
                    <SelectOption
                      data={GenderOptions}
                      selectedOption={
                        witnessGender
                          ? GenderOptions.find((g) => g.value === witnessGender)
                          : null
                      }
                      handleChange={(option) => {
                        setValue(
                          `incident_details_witnesses.${index}.gender`,
                          option?.value || null
                        );
                      }}
                      placeHolder="Select gender..."
                      isClearable
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-900 mb-1.5">
                      Address:
                    </label>
                    <input
                      type="text"
                      {...register(
                        `incident_details_witnesses.${index}.address`
                      )}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                      placeholder="Enter address..."
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
};
